import React from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  FormControl,
  FormControlLabel,
  InputLabel,
  ListSubheader,
  MenuItem,
  Select,
  Switch,
  TextField,
  Typography
} from '@mui/material';
import {
  AI_CHECK_TYPE_LIST,
  DETERMINISTIC_CHECK_TYPES,
  QUALITY_CRITERIA,
  STRICTNESS_OPTIONS,
  isAiCheck,
  CHECK_TYPES,
  DATE_OPERATORS,
  DISTANCE_UNITS,
  DURATION_UNITS,
  NUMERIC_OPERATORS,
  PRESENCE_MODES,
  PROXIMITY_MODES,
  RuleSourceConfig,
  RuleSpec,
  SOURCES_FOR_KIND,
  SOURCE_TYPE_LABELS,
  SlotDef,
  TEXT_OPERATORS,
  describeRule,
  reconcileInputs,
  slotsForSpec
} from 'utils/detectionCheckTypes';

/**
 * Form for a STRUCTURED detection rule.
 *
 * The rule is built by choosing a check type and filling in its slots, rather
 * than by writing a sentence for a model to interpret. Two things carry most of
 * the value here: source pickers are filtered to the kinds a slot can actually
 * accept (a date comparison won't offer you an image field), and the rule is
 * echoed back as plain English underneath — if that sentence reads wrong, the
 * rule IS wrong, and spotting it needs no understanding of the schema.
 */

interface RuleBuilderProps {
  spec: RuleSpec;
  onChange: (spec: RuleSpec) => void;
  steps: any[];
  resourceTypes: any[];
  /** From GET /v2/detection/providers — only offer what actually has a key. */
  providers?: {
    provider: string;
    label: string;
    available: boolean;
    reason: string | null;
    models: { id: string; label: string; default?: boolean }[];
    defaultModel: string | null;
  }[];
}

/** Slots may be plain strings on rules written before per-slot kinds existed. */
const normaliseSlot = (entry: any): { name: string; kind: 'text' | 'image' } =>
  typeof entry === 'string'
    ? { name: entry, kind: 'text' }
    : { name: String(entry?.name || ''), kind: entry?.kind === 'image' ? 'image' : 'text' };

const RuleBuilder: React.FC<RuleBuilderProps> = ({ spec, onChange, steps, resourceTypes, providers = [] }) => {
  const aiTier = isAiCheck(spec.checkType);
  const [modelOpen, setModelOpen] = React.useState(false);
  const def = CHECK_TYPES[spec.checkType];
  const slots = slotsForSpec(spec);

  const setParams = (patch: Record<string, any>) =>
    // Slots can depend on params (between needs bounds; "compare against now"
    // drops a slot), so re-derive inputs whenever params move.
    onChange(reconcileInputs({ ...spec, params: { ...spec.params, ...patch } }));

  const setSource = (slotName: string, patch: Partial<RuleSourceConfig>) =>
    onChange({
      ...spec,
      inputs: spec.inputs.map((i) => (i.slot === slotName ? { ...i, source: { ...i.source, ...patch } } : i))
    });

  const sourceFor = (slotName: string): RuleSourceConfig =>
    spec.inputs.find((i) => i.slot === slotName)?.source || ({ sourceType: 'custom' } as RuleSourceConfig);

  // ── helpers shared with the legacy form's expectations ────────────────────

  const stepFields = (stepUid?: string, kind?: string) => {
    const step = steps.find((s: any) => s.uid === stepUid);
    const fields = step?.component?.fields || [];
    if (kind === 'date') return fields.filter((f: any) => f.inputComponent === 'DATE');
    if (kind === 'image') return fields.filter((f: any) => ['CAMERA', 'FILE'].includes(f.inputComponent));
    return fields.filter((f: any) => !['CAMERA', 'FILE'].includes(f.inputComponent));
  };

  const resourceFields = (rtId?: string) => resourceTypes.find((r: any) => r.id === rtId || r._id === rtId)?.fields || [];

  /** Human name for a bound source — feeds the plain-English echo. */
  const sourceName = (slot: SlotDef): string => {
    const s = sourceFor(slot.name);
    if (s.sourceType === 'custom') return s.customValue ? `"${s.customValue}"` : slot.label.toLowerCase();
    const step = steps.find((x: any) => x.uid === s.stepUid);
    if (s.sourceType === 'step_location') return step ? `${step.title} location` : slot.label.toLowerCase();
    if (s.sourceType === 'step_submission_date') return step ? `${step.title} submitted-at` : slot.label.toLowerCase();
    if (s.sourceType === 'resource_type_field') {
      const rt = resourceTypes.find((r: any) => (r.id || r._id) === s.resourceTypeId);
      return s.fieldUid ? `${rt?.name ? `${rt.name} ` : ''}${s.fieldUid}` : slot.label.toLowerCase();
    }
    const field = step?.component?.fields?.find((f: any) => f.uid === s.fieldUid);
    if (step && field) return `${step.title} → ${field.question || field.name}`;
    return slot.label.toLowerCase();
  };

  const slotNames = Object.fromEntries(slots.map((s) => [s.name, sourceName(s)]));

  // ── renderers ─────────────────────────────────────────────────────────────

  const renderSourcePicker = (slot: SlotDef) => {
    const source = sourceFor(slot.name);
    const allowed = SOURCES_FOR_KIND[slot.kind] || [];

    return (
      <Box
        key={slot.name}
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          mb: 1,
          p: 1,
          bgcolor: 'grey.50',
          borderRadius: 1,
          border: '1px solid',
          borderColor: 'grey.200',
          flexWrap: 'wrap'
        }}
      >
        <Chip label={slot.label} size="small" color="primary" sx={{ fontWeight: 600, fontSize: '0.72rem' }} />

        <FormControl size="small" sx={{ minWidth: 150 }}>
          <InputLabel>From</InputLabel>
          <Select
            value={allowed.includes(source.sourceType) ? source.sourceType : ''}
            label="From"
            onChange={(e) =>
              setSource(slot.name, {
                sourceType: e.target.value as any,
                stepUid: undefined,
                fieldUid: undefined,
                resourceTypeId: undefined,
                customValue: '',
                customType: 'text'
              })
            }
          >
            {allowed.map((t) => (
              <MenuItem key={t} value={t}>
                {SOURCE_TYPE_LABELS[t] || t}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        {['step_text_field', 'step_date_field', 'step_image_field'].includes(source.sourceType) && (
          <>
            <FormControl size="small" sx={{ minWidth: 140, flex: 1 }}>
              <InputLabel>Step</InputLabel>
              <Select
                value={source.stepUid || ''}
                label="Step"
                onChange={(e) => setSource(slot.name, { stepUid: e.target.value, fieldUid: '' })}
              >
                {steps.map((s: any) => (
                  <MenuItem key={s.uid} value={s.uid}>
                    {s.title}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl size="small" sx={{ minWidth: 140, flex: 1 }}>
              <InputLabel>Field</InputLabel>
              <Select
                value={source.fieldUid || ''}
                label="Field"
                disabled={!source.stepUid}
                onChange={(e) => setSource(slot.name, { fieldUid: e.target.value })}
              >
                {(() => {
                  const kind =
                    source.sourceType === 'step_date_field' ? 'date' : source.sourceType === 'step_image_field' ? 'image' : 'text';
                  const fields = stepFields(source.stepUid, kind);
                  if (fields.length === 0)
                    return (
                      <MenuItem disabled>
                        <em>No {kind} fields in this step</em>
                      </MenuItem>
                    );
                  return fields.map((f: any) => (
                    <MenuItem key={f.uid} value={f.uid}>
                      {f.question || f.name}
                    </MenuItem>
                  ));
                })()}
              </Select>
            </FormControl>
          </>
        )}

        {['step_location', 'step_submission_date'].includes(source.sourceType) && (
          <FormControl size="small" sx={{ minWidth: 180, flex: 1 }}>
            <InputLabel>Step</InputLabel>
            <Select value={source.stepUid || ''} label="Step" onChange={(e) => setSource(slot.name, { stepUid: e.target.value })}>
              {steps.map((s: any) => (
                <MenuItem key={s.uid} value={s.uid}>
                  {s.title}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        )}

        {source.sourceType === 'resource_type_field' && (
          <>
            <FormControl size="small" sx={{ minWidth: 140, flex: 1 }}>
              <InputLabel>Resource type</InputLabel>
              <Select
                value={source.resourceTypeId || ''}
                label="Resource type"
                onChange={(e) => setSource(slot.name, { resourceTypeId: e.target.value, fieldUid: '' })}
              >
                {resourceTypes.map((rt: any) => (
                  <MenuItem key={rt.id || rt._id} value={rt.id || rt._id}>
                    {rt.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl size="small" sx={{ minWidth: 140, flex: 1 }}>
              <InputLabel>Field</InputLabel>
              <Select
                value={source.fieldUid || ''}
                label="Field"
                disabled={!source.resourceTypeId}
                onChange={(e) => setSource(slot.name, { fieldUid: e.target.value })}
              >
                {resourceFields(source.resourceTypeId).map((f: any) => (
                  <MenuItem key={f.name} value={f.name}>
                    {f.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </>
        )}

        {source.sourceType === 'custom' && (
          <TextField
            size="small"
            sx={{ flex: 1, minWidth: 160 }}
            label={slot.kind === 'location' ? 'Latitude, longitude' : 'Value'}
            placeholder={slot.kind === 'location' ? '53.4774, -2.2309' : slot.kind === 'number' ? '100' : 'Enter a value'}
            value={source.customValue || ''}
            onChange={(e) => setSource(slot.name, { customValue: e.target.value, customType: 'text' })}
          />
        )}

        {slot.hint && (
          <Typography variant="caption" color="text.secondary" sx={{ width: '100%' }}>
            {slot.hint}
          </Typography>
        )}
      </Box>
    );
  };

  const renderParams = () => {
    const p = spec.params || {};
    switch (spec.checkType) {
      case 'TEXT_COMPARE':
        return (
          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
            <FormControl size="small" sx={{ minWidth: 200 }}>
              <InputLabel>Comparison</InputLabel>
              <Select value={p.operator || 'equals'} label="Comparison" onChange={(e) => setParams({ operator: e.target.value })}>
                {TEXT_OPERATORS.map((o) => (
                  <MenuItem key={o.value} value={o.value}>
                    {o.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControlLabel
              control={<Switch size="small" checked={!!p.caseSensitive} onChange={(e) => setParams({ caseSensitive: e.target.checked })} />}
              label={<Typography variant="caption">Case-sensitive</Typography>}
            />
            <FormControlLabel
              control={<Switch size="small" checked={p.trim !== false} onChange={(e) => setParams({ trim: e.target.checked })} />}
              label={<Typography variant="caption">Ignore surrounding spaces</Typography>}
            />
          </Box>
        );

      case 'NUMERIC_COMPARE':
        return (
          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
            <FormControl size="small" sx={{ minWidth: 200 }}>
              <InputLabel>Comparison</InputLabel>
              <Select value={p.operator || 'gte'} label="Comparison" onChange={(e) => setParams({ operator: e.target.value })}>
                {NUMERIC_OPERATORS.map((o) => (
                  <MenuItem key={o.value} value={o.value}>
                    {o.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            {['eq', 'neq'].includes(p.operator) && (
              <TextField
                size="small"
                type="number"
                label="Tolerance (±)"
                sx={{ width: 150 }}
                value={p.tolerance ?? 0}
                onChange={(e) => setParams({ tolerance: e.target.value })}
                helperText="Only applies to equality"
              />
            )}
          </Box>
        );

      case 'DATE_COMPARE':
        return (
          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'flex-start' }}>
            <FormControl size="small" sx={{ minWidth: 160 }}>
              <InputLabel>Comparison</InputLabel>
              <Select value={p.operator || 'before'} label="Comparison" onChange={(e) => setParams({ operator: e.target.value })}>
                {DATE_OPERATORS.map((o) => (
                  <MenuItem key={o.value} value={o.value}>
                    {o.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl size="small" sx={{ minWidth: 190 }}>
              <InputLabel>Compare against</InputLabel>
              <Select
                value={p.referenceMode || 'source'}
                label="Compare against"
                onChange={(e) => setParams({ referenceMode: e.target.value })}
              >
                <MenuItem value="source">Another date field</MenuItem>
                <MenuItem value="now">The time the rule runs</MenuItem>
              </Select>
            </FormControl>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <TextField
                size="small"
                type="number"
                label={p.operator === 'within' ? 'Window' : 'Allowance'}
                sx={{ width: 110 }}
                value={p.offset?.value ?? ''}
                onChange={(e) =>
                  setParams({
                    offset: e.target.value === '' ? null : { value: e.target.value, unit: p.offset?.unit || 'hours' }
                  })
                }
                required={p.operator === 'within'}
              />
              <FormControl size="small" sx={{ minWidth: 110 }}>
                <InputLabel>Unit</InputLabel>
                <Select
                  value={p.offset?.unit || 'hours'}
                  label="Unit"
                  onChange={(e) => setParams({ offset: { value: p.offset?.value ?? '', unit: e.target.value } })}
                >
                  {DURATION_UNITS.map((o) => (
                    <MenuItem key={o.value} value={o.value}>
                      {o.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>
            <Typography variant="caption" color="text.secondary" sx={{ width: '100%' }}>
              {p.operator === 'within'
                ? 'How far either side of the comparison date still counts.'
                : 'Optional — shifts the comparison point, e.g. "before arrival + 24 hours". Leave blank to compare directly.'}
            </Typography>
          </Box>
        );

      case 'LOCATION_PROXIMITY':
        return (
          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
            <FormControl size="small" sx={{ minWidth: 170 }}>
              <InputLabel>Comparison</InputLabel>
              <Select value={p.mode || 'within'} label="Comparison" onChange={(e) => setParams({ mode: e.target.value })}>
                {PROXIMITY_MODES.map((o) => (
                  <MenuItem key={o.value} value={o.value}>
                    {o.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <TextField
              size="small"
              type="number"
              label="Distance"
              sx={{ width: 120 }}
              value={p.maxDistance?.value ?? ''}
              onChange={(e) => setParams({ maxDistance: { value: e.target.value, unit: p.maxDistance?.unit || 'km' } })}
            />
            <FormControl size="small" sx={{ minWidth: 130 }}>
              <InputLabel>Unit</InputLabel>
              <Select
                value={p.maxDistance?.unit || 'km'}
                label="Unit"
                onChange={(e) => setParams({ maxDistance: { value: p.maxDistance?.value ?? '', unit: e.target.value } })}
              >
                {DISTANCE_UNITS.map((o) => (
                  <MenuItem key={o.value} value={o.value}>
                    {o.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
        );

      case 'FIELD_PRESENCE':
        return (
          <FormControl size="small" sx={{ minWidth: 200 }}>
            <InputLabel>The field should</InputLabel>
            <Select value={p.require || 'present'} label="The field should" onChange={(e) => setParams({ require: e.target.value })}>
              {PRESENCE_MODES.map((o) => (
                <MenuItem key={o.value} value={o.value}>
                  {o.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        );

      case 'IMAGE_CONTAINS':
        return (
          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
            <TextField
              size="small"
              sx={{ flex: 1, minWidth: 240 }}
              label="The photo should show"
              placeholder="a sealed pallet with an intact shrink wrap"
              value={p.subject || ''}
              onChange={(e) => setParams({ subject: e.target.value })}
            />
            <FormControlLabel
              control={<Switch size="small" checked={!!p.mustBeAbsent} onChange={(e) => setParams({ mustBeAbsent: e.target.checked })} />}
              label={<Typography variant="caption">Flag when it IS shown instead</Typography>}
            />
          </Box>
        );

      case 'IMAGE_QUALITY':
        return (
          <FormControl size="small" fullWidth>
            <InputLabel>Must be</InputLabel>
            <Select
              multiple
              value={p.criteria || []}
              label="Must be"
              onChange={(e) => setParams({ criteria: e.target.value })}
              renderValue={(sel) => (
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                  {(sel as string[]).map((v) => (
                    <Chip key={v} size="small" label={QUALITY_CRITERIA.find((q) => q.value === v)?.label || v} />
                  ))}
                </Box>
              )}
            >
              {QUALITY_CRITERIA.map((q) => (
                <MenuItem key={q.value} value={q.value}>
                  {q.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        );

      case 'DOCUMENT_FIELD_EXTRACT':
        return (
          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
            <TextField
              size="small"
              sx={{ minWidth: 200 }}
              label="Document type"
              placeholder="driving licence"
              value={p.documentType || ''}
              onChange={(e) => setParams({ documentType: e.target.value })}
            />
            <TextField
              size="small"
              sx={{ minWidth: 200, flex: 1 }}
              label="Field to read"
              placeholder="expiry date"
              value={p.field || ''}
              onChange={(e) => setParams({ field: e.target.value })}
            />
            <Typography variant="caption" color="text.secondary" sx={{ width: '100%' }}>
              The value it reads is shown on the result, so a disputed answer can be checked. Pair this with a date or text comparison to
              decide anything from it.
            </Typography>
          </Box>
        );

      case 'DOCUMENT_AUTHENTICITY':
        return (
          <TextField
            size="small"
            fullWidth
            label="Document type"
            placeholder="certificate of conformity"
            value={p.documentType || ''}
            onChange={(e) => setParams({ documentType: e.target.value })}
          />
        );

      case 'SIGNATURE_PRESENT':
        return (
          <FormControlLabel
            control={<Switch size="small" checked={!!p.requireLegible} onChange={(e) => setParams({ requireLegible: e.target.checked })} />}
            label={<Typography variant="caption">The signature must also be readable as a name</Typography>}
          />
        );

      case 'SEMANTIC_MATCH':
        return (
          <FormControl size="small" sx={{ minWidth: 280 }}>
            <InputLabel>They must</InputLabel>
            <Select value={p.strictness || 'equivalent'} label="They must" onChange={(e) => setParams({ strictness: e.target.value })}>
              {STRICTNESS_OPTIONS.map((o) => (
                <MenuItem key={o.value} value={o.value}>
                  {o.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        );

      case 'CUSTOM_AI': {
        const slots: any[] = p.slots?.length ? p.slots : [{ name: 'input1', kind: 'text' }];
        const setSlot = (i: number, patch: any) =>
          setParams({ slots: slots.map((sl, idx) => (idx === i ? { ...normaliseSlot(sl), ...patch } : normaliseSlot(sl))) });

        return (
          <Box>
            <Alert severity="warning" sx={{ mb: 1.5, py: 0.5 }}>
              <Typography variant="caption">
                A written instruction is interpreted by a model, so the same input can produce different answers. Use a structured check
                where one fits.
              </Typography>
            </Alert>

            <TextField
              size="small"
              fullWidth
              multiline
              rows={3}
              label="Instruction"
              placeholder="Describe exactly what to check. Refer to the inputs below by name."
              value={p.instruction || ''}
              onChange={(e) => setParams({ instruction: e.target.value })}
            />

            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1.5, mb: 0.5 }}>
              Inputs — name them, and say whether each is text or a photo. Refer to them by name in the instruction above.
            </Typography>
            {slots.map((raw, i) => {
              const sl = normaliseSlot(raw);
              return (
                <Box key={i} sx={{ display: 'flex', gap: 1, alignItems: 'center', mb: 1 }}>
                  <TextField
                    size="small"
                    label="Name"
                    sx={{ width: 180 }}
                    value={sl.name}
                    onChange={(e) => setSlot(i, { name: e.target.value })}
                  />
                  <FormControl size="small" sx={{ width: 140 }}>
                    <InputLabel>Kind</InputLabel>
                    <Select value={sl.kind} label="Kind" onChange={(e) => setSlot(i, { kind: e.target.value })}>
                      <MenuItem value="text">Text</MenuItem>
                      <MenuItem value="image">Photo</MenuItem>
                    </Select>
                  </FormControl>
                  {slots.length > 1 && (
                    <Button
                      size="small"
                      color="error"
                      onClick={() => setParams({ slots: slots.filter((_, idx) => idx !== i).map(normaliseSlot) })}
                      sx={{ textTransform: 'none', minWidth: 0 }}
                    >
                      Remove
                    </Button>
                  )}
                </Box>
              );
            })}
            <Button
              size="small"
              onClick={() => setParams({ slots: [...slots.map(normaliseSlot), { name: `input${slots.length + 1}`, kind: 'text' }] })}
              sx={{ textTransform: 'none' }}
            >
              + Add input
            </Button>
          </Box>
        );
      }

      default:
        return null;
    }
  };

  /**
   * Collapsed by default and only on Tier C. A greyed-out model picker on a
   * date comparison would still imply the model matters, so it is absent there
   * rather than disabled.
   */
  const renderModelPicker = () => {
    if (!aiTier) return null;
    const chosen = spec.ai?.provider;
    const active = providers.find((x) => x.provider === chosen) || providers.find((x) => x.available);
    const summary = spec.ai?.model || active?.defaultModel || 'the default model';

    return (
      <Box sx={{ mt: 1 }}>
        <Button size="small" variant="text" onClick={() => setModelOpen((v) => !v)} sx={{ textTransform: 'none', p: 0 }}>
          {modelOpen ? '▾' : '▸'} Model: {summary}
        </Button>
        {modelOpen && (
          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mt: 1, pl: 1.5 }}>
            <FormControl size="small" sx={{ minWidth: 180 }}>
              <InputLabel>Provider</InputLabel>
              <Select
                value={chosen || ''}
                label="Provider"
                onChange={(e) => {
                  const next = providers.find((x) => x.provider === e.target.value);
                  onChange({ ...spec, ai: { ...spec.ai, provider: e.target.value as any, model: next?.defaultModel || undefined } });
                }}
              >
                <MenuItem value="">
                  <em>Default for this deployment</em>
                </MenuItem>
                {providers.map((prov) => (
                  <MenuItem key={prov.provider} value={prov.provider} disabled={!prov.available}>
                    {prov.label}
                    {!prov.available && ` — ${prov.reason}`}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl size="small" sx={{ minWidth: 220 }} disabled={!active?.models?.length}>
              <InputLabel>Model</InputLabel>
              <Select
                value={spec.ai?.model || ''}
                label="Model"
                onChange={(e) => onChange({ ...spec, ai: { ...spec.ai, model: e.target.value } })}
              >
                <MenuItem value="">
                  <em>Default</em>
                </MenuItem>
                {(active?.models || []).map((m) => (
                  <MenuItem key={m.id} value={m.id}>
                    {m.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl size="small" sx={{ minWidth: 200 }}>
              <InputLabel>If the call fails</InputLabel>
              <Select
                value={spec.ai?.fallback || 'auto'}
                label="If the call fails"
                onChange={(e) => onChange({ ...spec, ai: { ...spec.ai, fallback: e.target.value as any } })}
              >
                <MenuItem value="auto">Try another provider</MenuItem>
                <MenuItem value="none">Record it as Inconclusive</MenuItem>
              </Select>
            </FormControl>

            <Typography variant="caption" color="text.secondary" sx={{ width: '100%' }}>
              Fallback only applies when the provider can’t be reached — never to a result you disagree with. Whichever model answered is
              recorded on the result.
            </Typography>
          </Box>
        )}
      </Box>
    );
  };

  return (
    <Box>
      {/* 1 — what to check */}
      <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 0.25 }}>
        What to check
      </Typography>
      <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', mb: 0.5, flexWrap: 'wrap' }}>
        <FormControl size="small" sx={{ minWidth: 220 }}>
          <InputLabel>Check type</InputLabel>
          <Select
            value={spec.checkType}
            label="Check type"
            onChange={(e) => {
              // Switching type replaces params and slots wholesale — carrying
              // the old ones over produces a rule that looks configured but
              // references inputs the new check doesn't have.
              const next = CHECK_TYPES[e.target.value as keyof typeof CHECK_TYPES];
              onChange(reconcileInputs({ ...spec, checkType: next.type, params: next.defaults(), inputs: [] }));
            }}
          >
            {/* Grouped so the exact checks read as the default choice and the
                AI ones as the deliberate exception, not two equal columns. */}
            <ListSubheader>Exact checks — no AI</ListSubheader>
            {DETERMINISTIC_CHECK_TYPES.map((d) => (
              <MenuItem key={d.type} value={d.type}>
                {d.label}
              </MenuItem>
            ))}
            <ListSubheader>Needs judgement — uses AI</ListSubheader>
            {AI_CHECK_TYPE_LIST.map((d) => (
              <MenuItem key={d.type} value={d.type}>
                {d.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <Chip label={def?.engineNote} size="small" color={aiTier ? 'warning' : 'success'} variant="outlined" sx={{ fontWeight: 500 }} />
      </Box>
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
        {def?.summary}
      </Typography>
      {renderModelPicker()}
      <Box sx={{ mb: 2 }} />

      {/* 2 — inputs */}
      <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 0.25 }}>
        Inputs
      </Typography>
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
        Only sources that can supply the right kind of value are offered.
      </Typography>
      {slots.map(renderSourcePicker)}

      {/* 3 — how to compare */}
      <Typography variant="subtitle2" sx={{ fontWeight: 600, mt: 2, mb: 1 }}>
        How to compare
      </Typography>
      {renderParams()}

      {/* 4 — polarity */}
      <Typography variant="subtitle2" sx={{ fontWeight: 600, mt: 2.5, mb: 1 }}>
        When to flag it
      </Typography>
      <FormControl size="small" sx={{ minWidth: 320 }}>
        <InputLabel>Outcome</InputLabel>
        <Select value={spec.expect} label="Outcome" onChange={(e) => onChange({ ...spec, expect: e.target.value as any })}>
          <MenuItem value="PASS_WHEN_TRUE">Match when the condition holds</MenuItem>
          <MenuItem value="PASS_WHEN_FALSE">No Match when the condition holds</MenuItem>
        </Select>
      </FormControl>

      {/* The safety net: if this sentence reads wrong, the rule is wrong. */}
      <Alert severity="info" icon={false} sx={{ mt: 1.5, py: 0.75, bgcolor: 'primary.50' }}>
        <Typography variant="body2" sx={{ fontWeight: 600, fontSize: '0.82rem' }}>
          {describeRule(spec, slotNames)}
        </Typography>
      </Alert>

      {/* Confidence threshold — Tier C only. Deterministic checks are exact,
          so a threshold there would be meaningless. */}
      {aiTier && (
        <>
          <Typography variant="subtitle2" sx={{ fontWeight: 600, mt: 2.5, mb: 1 }}>
            How sure it has to be
          </Typography>
          <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
            <TextField
              size="small"
              type="number"
              label="Minimum confidence (%)"
              sx={{ width: 200 }}
              value={spec.minConfidence ?? spec.params?.minConfidence ?? 70}
              onChange={(e) => onChange({ ...spec, minConfidence: Number(e.target.value) })}
            />
            <Typography variant="caption" color="text.secondary" sx={{ flex: 1, minWidth: 240 }}>
              Below this the result is recorded as Inconclusive for someone to look at, rather than acted on. Self-reported confidence runs
              optimistic, so treat it as a dial to tune against real results, not a probability.
            </Typography>
          </Box>
        </>
      )}

      {/* 5 — missing data */}
      <Typography variant="subtitle2" sx={{ fontWeight: 600, mt: 2.5, mb: 1 }}>
        If an input can’t be read
      </Typography>
      <FormControl size="small" sx={{ minWidth: 260 }}>
        <InputLabel>Then</InputLabel>
        <Select value={spec.onMissingInput} label="Then" onChange={(e) => onChange({ ...spec, onMissingInput: e.target.value as any })}>
          <MenuItem value="inconclusive">Record it as Inconclusive</MenuItem>
          <MenuItem value="fail">Record it as No Match</MenuItem>
          <MenuItem value="skip">Say nothing</MenuItem>
        </Select>
      </FormControl>
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
        A step that simply hasn’t been submitted yet is always deferred, not treated as missing.
      </Typography>
    </Box>
  );
};

export default RuleBuilder;
